import { useEffect, useId, useRef, useState } from "react";
import type { ButtonHTMLAttributes, InputHTMLAttributes, KeyboardEvent, ReactNode, RefObject, SelectHTMLAttributes } from "react";
import { errorMessage } from "../api/hooks";

export const cx = (...classes: (string | false | null | undefined)[]) => classes.filter(Boolean).join(" ");

type Variant = "primary" | "secondary" | "danger" | "ghost";
// Every button is a pill (Figma). `secondary` is the outlined crimson pill the design uses for most
// actions (Preview, Pause, Open full guide, + Protect hours); `ghost` the grey fill (Clear); `primary`
// the one filled action per page, on primary-strong (#C9143B, about 5.8:1 with white) so a 14px label passes AA.
const BUTTON: Record<Variant, string> = {
  primary: "bg-hs-primary-strong text-hs-on-primary hover:brightness-90",
  secondary: "border border-hs-primary bg-hs-surface text-hs-primary hover:bg-hs-primary-soft/60",
  danger: "bg-hs-danger text-white hover:brightness-90",
  ghost: "bg-hs-chip text-hs-text hover:bg-hs-border-strong/70",
};
export function Button({ variant = "primary", size = "md", className, type = "button", ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: "sm" | "md" }) {
  return <button type={type} {...props} className={cx("inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-semibold transition disabled:cursor-not-allowed disabled:opacity-50", size === "sm" ? "h-[38px] px-4 text-[13px]" : "h-[42px] px-5 text-sm", BUTTON[variant], className)} />;
}

type Tone = "neutral" | "primary" | "success" | "warning" | "danger" | "info";
const BADGE: Record<Tone, string> = {
  neutral: "border border-hs-text/50 bg-hs-surface text-hs-text/60", primary: "bg-hs-primary-soft text-hs-primary", success: "bg-hs-success-soft text-hs-success",
  warning: "bg-hs-warning-soft text-hs-warning", danger: "bg-hs-danger-soft text-hs-danger", info: "bg-hs-info-soft text-hs-info",
};
/** A soft-filled pill chip (Figma); the neutral one is outlined so "No change" reads quieter. For action types and rule scope. */
export function Badge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return <span className={cx("inline-flex h-7 items-center whitespace-nowrap rounded-full px-4 text-[13px] font-semibold", BADGE[tone])}>{children}</span>;
}

/**
 * A section card: 20px radius, bordered. `title`/`subtitle`/`actions` make a padded header; the body
 * sits under a divider. `padding="none"` for tables and for bodies made of `PanelRow`s, which carry
 * their own dividers. `footer` is a bottom action row.
 */
export function Panel({ title, subtitle, actions, footer, padding = "md", className, children }: { title?: ReactNode; subtitle?: ReactNode; actions?: ReactNode; footer?: ReactNode; padding?: "md" | "none"; className?: string; children: ReactNode }) {
  const header = Boolean(title || actions);
  return (
    <section className={cx("rounded-2xl border border-hs-border bg-hs-surface", className)}>
      {header && (
        <header className="flex flex-wrap items-center justify-between gap-3 p-6">
          {title && (
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-hs-text">{title}</h2>
              {subtitle && <p className="mt-1 text-sm font-medium text-hs-text-muted">{subtitle}</p>}
            </div>
          )}
          {actions && <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={padding === "none" ? undefined : cx("px-6 pb-6", header ? "border-t border-hs-border pt-4" : "pt-6")}>{children}</div>
      {footer && <footer className="flex flex-wrap items-center gap-2 border-t border-hs-border px-6 py-4">{footer}</footer>}
    </section>
  );
}

/** One setting row inside a `padding="none"` Panel: text on the left, the control on the right, divider above. */
export function PanelRow({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cx("flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-t border-hs-border px-6 py-4", className)}>{children}</div>;
}

/** The label + hint pair a `PanelRow` starts with. */
export function RowText({ label, hint, className }: { label: ReactNode; hint?: ReactNode; className?: string }) {
  return (
    <div className={cx("min-w-0", className)}>
      <p className="text-base font-semibold text-hs-text">{label}</p>
      {hint && <p className="mt-0.5 text-xs font-medium text-hs-text-muted">{hint}</p>}
    </div>
  );
}

/**
 * A "?" button that reveals a short explanation in the flow beneath it. No floating layer, so it
 * reads on a phone and needs no positioning. `hint` shares the button's line.
 */
export function Help({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <div className="mt-1 text-xs text-hs-text-muted">
      <span className="inline-flex items-center gap-1.5">
        {hint && <span>{hint}</span>}
        <button type="button" aria-label={label} aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}
          className={cx("inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold leading-none", open ? "bg-hs-primary-soft text-hs-primary" : "bg-hs-icon-well text-hs-text-muted hover:bg-hs-primary-soft hover:text-hs-primary")}>
          ?
        </button>
      </span>
      {open && <span id={id} role="note" className="mt-2 block max-w-prose text-[13px] leading-5">{children}</span>}
    </div>
  );
}

/**
 * The control stays wrapped in its `<label>` (implicit association, which the tests rely on); the
 * hint and the optional help button sit under it as siblings so the button is never inside a label.
 */
export function Field({ label, hint, help, className, children }: { label: string; hint?: string; help?: ReactNode; className?: string; children: ReactNode }) {
  return (
    <div className={cx("block", className)}>
      <label className="block">
        <span className="mb-1 block text-base font-medium text-hs-text">{label}</span>
        {children}
      </label>
      {help ? <Help label={`What does "${label}" mean?`} hint={hint}>{help}</Help> : hint && <span className="mt-1 block text-xs text-hs-text-muted">{hint}</span>}
    </div>
  );
}
const CONTROL = "h-[46px] w-full rounded-md border border-hs-border-strong bg-hs-surface px-3 text-base font-medium text-hs-text placeholder:text-hs-text/60 focus:border-hs-text-muted disabled:bg-hs-surface-alt";
export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) { return <input {...props} className={cx(CONTROL, className)} />; }
export function Select({ className, ...props }: SelectHTMLAttributes<HTMLSelectElement>) { return <select {...props} className={cx(CONTROL, "hs-select", className)} />; }

/**
 * The Figma dropdown: a 14px-radius white box with a small caption over the chosen value and the
 * chevron at the right. The `<select>` fills the box, so the whole thing is the click target, and the
 * caption is its label (the control is inside the `<label>`).
 */
export function PickBox({ label, className, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { label: string }) {
  return (
    <label className={cx("relative block rounded-lg border-[1.5px] border-hs-control bg-hs-surface", className)}>
      <span className="pointer-events-none absolute left-4 top-2.5 text-[11px] font-medium leading-none text-hs-text-muted">{label}</span>
      <select {...props} className="hs-pick block h-[61px] w-full bg-transparent pb-2.5 pl-4 pt-6 text-sm font-semibold text-hs-text" />
    </label>
  );
}

/** A compact Figma dropdown with no caption: the control for a settings row or a word in a sentence. */
export function Pick({ className, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cx("hs-pick h-[42px] min-w-[8rem] rounded-lg border-[1.5px] border-hs-control bg-hs-surface pl-4 text-sm font-semibold text-hs-text focus:border-hs-primary", className)} />;
}

export function Toggle({ checked, onChange, label, disabled }: { checked: boolean; onChange: (next: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} onClick={() => onChange(!checked)}
      className={cx("relative h-[25px] w-[46px] shrink-0 rounded-full transition-colors disabled:opacity-50", checked ? "bg-hs-primary" : "bg-hs-text-subtle")}>
      <span className={cx("absolute left-[3px] top-[3px] h-[19px] w-[19px] rounded-full bg-white shadow-sm transition-transform", checked && "translate-x-[21px]")} />
    </button>
  );
}

/** The Figma's round grey "x" (remove a rule, close the guide). 28px well; the icon is the downloaded asset. */
export function CloseButton({ label, className, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button type="button" aria-label={label} {...props} className={cx("inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-hs-icon-well hover:bg-hs-primary-soft disabled:cursor-not-allowed disabled:opacity-50", className)}>
      <img src="/ui/close.svg" alt="" width={15} height={15} />
    </button>
  );
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Dialog({ open, title, onClose, children, footer }: { open: boolean; title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  return open ? <OpenDialog title={title} onClose={onClose} footer={footer}>{children}</OpenDialog> : null;
}

/**
 * A right-hand panel over the page (the rules preview): an `aside`, not a dialog, so a confirm dialog
 * can still open above it. Escape closes it, the page behind stops scrolling, focus moves in on open
 * and back to the opener on close. Mount it only while open.
 */
export function SidePanel({ label, title, intro, width = "max-w-[720px]", onClose, children }: {
  label: string; title: string; intro?: ReactNode; width?: string; onClose: () => void; children: ReactNode;
}) {
  const panel = useRef<HTMLElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const opener = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") close.current(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = previous; document.removeEventListener("keydown", onKey); opener?.focus?.(); };
  }, []);
  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside ref={panel} tabIndex={-1} aria-label={label} className={cx("absolute inset-y-0 right-0 flex w-full flex-col bg-hs-surface shadow-lg outline-none", width)}>
        <div className="relative shrink-0 px-8 pt-8">
          <CloseButton label={`Close ${label.toLowerCase()}`} onClick={onClose} className="absolute right-6 top-6" />
          <h2 className="pr-10 text-lg font-bold text-hs-text">{title}</h2>
          {intro}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-8 pt-6">{children}</div>
      </aside>
    </div>
  );
}

/**
 * Modal focus for anything mounted only while open (dialogs, the mobile drawer): focus moves into the
 * panel on mount and back to the opener on unmount; the returned handler keeps Tab inside and closes on
 * Escape. Mount the panel conditionally so the effect runs exactly on open and close.
 */
export function useModalFocus(panel: RefObject<HTMLElement | null>, onClose: () => void) {
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const first = panel.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel.current)?.focus();
    return () => opener?.focus?.();
  }, [panel]);
  return (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "Escape") { e.stopPropagation(); onClose(); return; }
    if (e.key !== "Tab" || !panel.current) return;
    // Keep Tab inside: wrap from the last focusable to the first and back.
    const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) { e.preventDefault(); return; }
    const [head, tail] = [items[0], items[items.length - 1]];
    const active = document.activeElement;
    if (e.shiftKey && (active === head || active === panel.current)) { e.preventDefault(); tail.focus(); }
    else if (!e.shiftKey && active === tail) { e.preventDefault(); head.focus(); }
  };
}

/** Mounted only while open, so the focus effects run exactly on open and close. */
function OpenDialog({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  const panel = useRef<HTMLDivElement>(null);
  const onKeyDown = useModalFocus(panel, onClose);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 p-3 sm:items-center" onClick={onClose}>
      <div ref={panel} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="dialog-title" onKeyDown={onKeyDown}
        className="w-full max-w-lg rounded-2xl border border-hs-border bg-hs-surface p-6 shadow-lg outline-none" onClick={(e) => e.stopPropagation()}>
        <h2 id="dialog-title" className="text-lg font-bold text-hs-text">{title}</h2>
        <div className="mt-3 space-y-3 text-sm text-hs-text">{children}</div>
        {footer && <div className="mt-6 flex flex-wrap justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

export function Spinner({ label = "Loading…" }: { label?: string }) {
  return (
    <div role="status" className="flex items-center gap-2 text-[13px] text-hs-text-muted">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-hs-border-strong border-t-hs-primary" aria-hidden="true" />
      {label}
    </div>
  );
}

const BANNER: Record<"info" | "success" | "warning" | "danger", string> = {
  info: "border-hs-info/40 bg-hs-info-soft/40 text-hs-info", success: "border-hs-success/40 bg-hs-success-soft text-hs-success",
  warning: "border-hs-warning/40 bg-hs-warning-soft text-hs-warning", danger: "border-hs-danger/40 bg-hs-danger-soft text-hs-danger",
};
/** Transient notices and the corporate-pause strip. Not for permanent state; the rail carries that. */
export function Banner({ tone, children }: { tone: keyof typeof BANNER; children: ReactNode }) {
  return <div role="status" className={cx("rounded-lg border px-4 py-3 text-sm font-medium", BANNER[tone])}>{children}</div>;
}

export function EmptyState({ title, body }: { title: string; body?: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-hs-border-strong p-8 text-center">
      <p className="text-base font-semibold text-hs-text">{title}</p>
      {body && <p className="mt-1 text-sm text-hs-text-muted">{body}</p>}
    </div>
  );
}

export function PageHeader({ title, subtitle, meta, actions }: { title: string; subtitle?: string; meta?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0 max-w-[52rem]">
        <h1 className="text-[30px] font-bold leading-tight text-hs-text">{title}</h1>
        {subtitle && <p className="mt-1.5 text-[15px] font-medium text-hs-text-muted">{subtitle}</p>}
        {meta && <p className="mt-2 text-sm font-medium text-hs-text tabular-nums">{meta}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

const DELTA: Record<"neutral" | "danger" | "success", string> = { neutral: "", danger: "text-hs-danger", success: "text-hs-success" };
/**
 * A number with a label, in an 18px-radius card. `delta` is short text ("+12%"); `deltaTone` colours it only
 * for metrics where direction has a meaning. `size="lg"` is the Results figure (32px); `tone` colours the figure.
 */
export function Stat({ label, value, detail, delta, deltaTone = "neutral", size = "md", tone = "neutral" }: { label: string; value: ReactNode; detail?: ReactNode; delta?: string; deltaTone?: "neutral" | "danger" | "success"; size?: "md" | "lg"; tone?: "neutral" | "danger" | "success" }) {
  return (
    // A section, like Panel, so a page can treat a tile and a card alike.
    <section className="rounded-xl border border-hs-border bg-hs-surface px-5 py-4">
      <p className="text-sm font-medium text-hs-text-muted">{label}</p>
      <div className={cx("mt-1 font-semibold leading-tight tabular-nums", size === "lg" ? "text-[32px]" : "text-2xl", DELTA[tone] || "text-hs-text")}>{value}</div>
      {(detail || delta) && <div className="mt-1 text-sm font-medium text-hs-text/70">{delta && <span className={cx("mr-2 font-semibold", DELTA[deltaTone])}>{delta}</span>}{detail}</div>}
    </section>
  );
}

/** A row of filters above a table. Children are usually `Field`s; give each a width (`className="w-40"`). */
export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("mb-4 flex flex-wrap items-end gap-3 border-b border-hs-border pb-4", className)}>{children}</div>;
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  return <Banner tone="danger">{errorMessage(error)}</Banner>;
}
